import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { Images } from "../../../utils/Images";
import { useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard";
import { getVendorShareDetails } from "../../../api/services/vendorService";

const sanitizeUrl = (url) => String(url || "").replace(/%22$/i, "").replace(/"+$/, "");
const stripHtml = (value) => String(value || "").replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n").replace(/<[^>]+>/g, "").trim();
const getInitials = (name) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "SV";

const VendorProductsSection = ({ vendor, isUnavailableVendor = false }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 767.95px)");
  const isTablet = useMediaQuery("(min-width:768px) and (max-width:899.95px)");
  const isLaptop = useMediaQuery("(min-width:900px) and (max-width:1199.95px)");
  const isDesktop = useMediaQuery("(min-width: 1200px)");
  const isMiddleScreen = isTablet || isLaptop;

  const productSection = Array.isArray(vendor?.vendorProducts) ? vendor.vendorProducts[0] : null;
  const products = useMemo(
    () =>
      Array.isArray(productSection?.products)
        ? productSection.products.map((item) => ({
            id: item.id,
            image: sanitizeUrl(item.image),
            title: item.title || "",
            description: item.description || "",
            price: item.price || "0",
            quantity: item.quantity || "",
            tagField: vendor?.tag_field || "Feeds 50+ Children",
          }))
        : [],
    [productSection, vendor?.tag_field]
  );

  const descriptionParagraphs = stripHtml(vendor?.main_description)
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
  const projectDescriptions = Array.isArray(vendor?.projectDescriptions)
    ? vendor.projectDescriptions.filter((item) => item && (item.status === 1 || item.status === true))
    : [];
  const verifiedVendor = vendor?.verified === 1 || vendor?.verified === true;
  const verifiedTrust = vendor?.trust_verified === 1 || vendor?.trust_verified === true;
  const [shareDetails, setShareDetails] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchShareDetails = async () => {
      if (!vendor?.id) {
        if (mounted) setShareDetails(null);
        return;
      }
      try {
        const response = await getVendorShareDetails(vendor.id);
        const payload = response?.data?.data || response?.data || response;
        if (!mounted) return;
        setShareDetails(payload || null);
      } catch (error) {
        if (!mounted) return;
        setShareDetails(null);
      }
    };
    fetchShareDetails();
    return () => {
      mounted = false;
    };
  }, [vendor?.id]);

  const localSocialHandles = useMemo(() => {
    const first = Array.isArray(vendor?.socialMediaLinks) ? vendor.socialMediaLinks[0] : null;
    return {
      facebook: first?.facebook === true,
      linkedin: first?.linkedin === true,
      twitter: first?.twitter === true,
      whatsapp: first?.whatsapp === true,
    };
  }, [vendor?.socialMediaLinks]);

  const socialHandles = shareDetails?.social_handles || localSocialHandles;
  const shareLinks = shareDetails?.share_links || {};
  const shareIcons = [
    { key: "facebook", icon: Images.facebook },
    { key: "linkedin", icon: Images.linkedin },
    { key: "twitter", icon: Images.twitter },
    { key: "whatsapp", icon: Images.whatsapp },
  ];
  const visibleShareIcons = shareIcons.filter((item) => socialHandles?.[item.key] === true);

  const productsHeaderAndGrid = (
    <Box
      sx={{
        background: "#F7F7F7",
        borderRadius: isMobile ? "16px" : "26px",
        p: isDesktop ? "30px 24px 24px" : isTablet ? "24px 20px" : "20.27px 16px 20px",
        mb: isMobile ? "24px" : "30px",
        overflow: isMobile ? "hidden" : "visible",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "10px" : "12px", mb: "12px" }}>
        <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: "#8EA946" }} />
        <Typography sx={{ fontWeight: 400, fontSize: isMobile ? "10px" : "14px", lineHeight: isMobile ? 1 : "18.2px", letterSpacing: isMobile ? "2px" : "2.1px", textTransform: "uppercase", color: "#131313" }}>
          Products By This Vendor
        </Typography>
      </Box>

      <Typography sx={{ fontWeight: 700, fontSize: isDesktop ? "36px" : isTablet ? "30px" : "18px", lineHeight: isMobile ? "29.124px" : "1.4", letterSpacing: "-0.36px", color: "#1D1D1B", maxWidth: isMobile ? "100%" : "805px", mb: isMobile ? "14px" : "10px" }}>
        {productSection?.section_title || "Buy vendor products"}
      </Typography>
      <Typography sx={{ fontWeight: 400, fontSize: isMobile ? "12px" : "16px", lineHeight: isMobile ? "19.416px" : "25.888px", letterSpacing: isMobile ? "-0.24px" : "normal", color: "#7D7D7D", maxWidth: isMobile ? "100%" : "877px", mb: "24px" }}>
        {productSection?.section_description || "Choose products to contribute."}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: isMobile ? "nowrap" : "wrap",
          gap: isMobile ? "14px" : "16px",
          justifyContent: "left",
          overflowX: isMobile ? "auto" : "visible",
          overflowY: "hidden",
          width: isMobile ? "calc(100% + 32px)" : "100%",
          ml: isMobile ? "-16px" : 0,
          pl: isMobile ? "16px" : 0,
          pr: isMobile ? "16px" : 0,
          boxSizing: "border-box",
          "&::-webkit-scrollbar": { display: isMobile ? "none" : "initial" },
          scrollbarWidth: isMobile ? "none" : "auto",
        }}
      >
        {products.map((item) => (
          <Box key={item.id} sx={{ width: isMobile ? "286px" : "281px", flex: isMobile ? "0 0 286px" : "0 0 281px" }}>
            <ProductCard
              item={item}
              onBuy={() => {
                if (isUnavailableVendor) return;
                navigate(`/small-vendors/payment/${vendor?.id}`);
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );

  const descriptionAndStory = (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "8px" : "18px", mb: isMobile ? "14px" : "20px", width: "100%" }}>
        <Typography sx={{ fontWeight: 700, fontSize: isDesktop ? "36px" : isTablet ? "30px" : "18px", lineHeight: isMobile ? "29.124px" : "50.2px", letterSpacing: isMobile ? "-0.18px" : "-0.36px", color: "#1D1D1B", maxWidth: isMobile ? "100%" : "930px" }}>
          {vendor?.title || ""}
        </Typography>
        {descriptionParagraphs.map((paragraph, index) => (
          <Typography key={`${paragraph}-${index}`} sx={{ fontWeight: 400, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "19.416px" : "22.652px", letterSpacing: isMobile ? "-0.24px" : "-0.28px", color: "#666666", maxWidth: isMobile ? "100%" : "923px" }}>
            {paragraph}
          </Typography>
        ))}
      </Box>

      {projectDescriptions.map((item) => (
        <Box key={item.id} sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "8px" : "8px", mb: isMobile ? "14px" : "14px", width: "100%" }}>
          <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "14px" : "16px", lineHeight: isMobile ? "normal" : 1, letterSpacing: isMobile ? "-0.28px" : "-0.32px", color: "#1A1A19", textTransform: "capitalize" }}>
            {item.title}
          </Typography>
          <Typography sx={{ fontWeight: 400, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "19.416px" : "22.652px", letterSpacing: isMobile ? "-0.24px" : "-0.28px", color: "#666666", maxWidth: isMobile ? "100%" : "923px" }}>
            {item.description}
          </Typography>
        </Box>
      ))}
    </>
  );

  return (
    <Box sx={{ width: isDesktop ? "94.44%" : "100%", maxWidth: "1440px", mx: "auto", px: isDesktop ? 0 : isMobile ? 0 : "16px", mb: isMobile ? "36px" : "60px" }}>
      <Box sx={{ display: "flex", alignItems: isDesktop ? "flex-start" : "stretch", gap: isMobile ? "0px" : isDesktop ? "22px" : isLaptop ? "20px" : "24px", flexDirection: isDesktop ? "row" : "column" }}>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          {productsHeaderAndGrid}
          {!isMobile && descriptionAndStory}
        </Box>

        <Box
          sx={{
            width: isDesktop ? "403px" : "100%",
            position: isDesktop ? "sticky" : "static",
            top: isDesktop ? "24px" : "auto",
            display: isMiddleScreen ? "grid" : "flex",
            gridTemplateColumns: isMiddleScreen ? "repeat(2, minmax(0, 1fr))" : "none",
            flexDirection: "column",
            gap: isMobile ? "12px" : "14px",
            alignSelf: "flex-start",
            px: isMobile ? "16px" : 0,
          }}
        >
          <Box sx={{ background: "#FFFFFF", border: "1px solid #E0EAC8", borderRadius: isMobile ? "12px" : "16px", boxShadow: "0px 10px 26px rgba(0,0,0,0.04)", p: isMobile ? "18px 16px" : "20px 16px", display: "flex", flexDirection: "column", gap: isMobile ? "16.792px" : "20px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "5.597px" : "14px" }}>
              <Box component="img" src={Images.greenLightning} alt="support" sx={{ width: isMobile ? "18px" : "22px", height: isMobile ? "18px" : "22px" }} />
              <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "14px" : "18px", lineHeight: isMobile ? "11.195px" : "29.124px", letterSpacing: isMobile ? "-0.28px" : "normal", color: "#1D1D1B", textTransform: "capitalize" }}>
                {vendor?.cart || "Support Ramesh's Cart"}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                if (isUnavailableVendor) return;
                navigate(`/small-vendors/payment/${vendor?.id}`);
              }}
              disabled={isUnavailableVendor}
              sx={{
                width: "100%",
                height: isMobile ? "40px" : "50px",
                borderRadius: isMobile ? "6.233px" : "9.284px",
                background: "#F04951",
                boxShadow: isMobile ? "0px 2.493px 0px 0px #141414" : "0px 3.714px 0px 0px #141414",
                textTransform: "none",
                fontWeight: 700,
                fontSize: isMobile ? "12px" : "14px",
                lineHeight: isMobile ? "8.726px" : "12.998px",
                "&:hover": { background: "#F04951" },
                "&.Mui-disabled": {
                  color: "#FFFFFF",
                  opacity: 0.65,
                  background: "#F04951",
                },
              }}
            >
              {Number(vendor?.closed_vendor) === 1 ? "Vendor Closed" : "Buy Now"}
            </Button>
            {visibleShareIcons.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? "13px" : "12px", flexWrap: "nowrap", width: "100%" }}>
                <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? 1 : "18px", color: "#1D1D1B", textTransform: "capitalize", whiteSpace: "nowrap" }}>
                  Share This Campaign:
                </Typography>
                <Box sx={{ display: "flex", gap: isMobile ? "8.633px" : "16px" }}>
                  {visibleShareIcons.map((item) => (
                    <Box
                      key={item.key}
                      onClick={() => {
                        const shareUrl = shareLinks?.[item.key];
                        if (!shareUrl) return;
                        window.open(shareUrl, "_blank", "noopener,noreferrer");
                      }}
                      sx={{ width: isMobile ? "28.777px" : "37.136px", height: isMobile ? "28.777px" : "37.136px", border: isMobile ? "0.719px solid #E8E8E8" : "0.928px solid #E8E8E8", borderRadius: isMobile ? "7.194px" : "9.284px", display: "grid", placeItems: "center", cursor: "pointer" }}
                    >
                      <Box component="img" src={item.icon} alt={item.key} sx={{ width: isMobile ? "17.266px" : "22.282px", height: isMobile ? "17.266px" : "22.282px" }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ background: "#FFFFFF", border: "1px solid #E0EAC8", borderRadius: isMobile ? "12px" : "16px", boxShadow: "0px 10px 26px rgba(0,0,0,0.04)", p: isMobile ? "18px 16px" : "20px 16px", display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "20px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "6px" : "10px" }}>
              <Box component="img" src={Images.greenShop} alt="ngo" sx={{ width: "20px", height: "20px" }} />
              <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "14px" : "18px", lineHeight: isMobile ? "11.195px" : 1, letterSpacing: isMobile ? "-0.28px" : "normal", color: "#1D1D1B", textTransform: "capitalize" }}>
                {vendor?.trust_name || "NGO Partner"}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 400, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "19.416px" : "22.652px", letterSpacing: isMobile ? "-0.24px" : "-0.28px", color: "#666666" }}>
              {vendor?.trust_description || ""}
            </Typography>
            {verifiedTrust && (
              <Box sx={{ width: "fit-content", px: "16px", py: "8px", borderRadius: "36px", border: "0.267px solid #D1DFAD", background: "#F5F8ED", display: "flex", alignItems: "center", gap: "8px" }}>
                <Box component="img" src={Images.greenBgTick} alt="verified" sx={{ width: "16px", height: "16px" }} />
                <Typography sx={{ fontWeight: 600, fontSize: "12px", lineHeight: "18px", color: "#8EA946", textTransform: "capitalize" }}>
                  Verified Partner
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ background: "#FFFFFF", border: "1px solid #E0EAC8", borderRadius: isMobile ? "12px" : "16px", boxShadow: "0px 10px 26px rgba(0,0,0,0.04)", p: isMobile ? "18px 16px" : "20px 16px", display: "flex", flexDirection: "column", gap: isMobile ? "16.792px" : "20px" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: isMobile ? "8px" : "16px" }}>
              {sanitizeUrl(vendor?.vendor_profile_photo) ? (
                <Box
                  component="img"
                  src={sanitizeUrl(vendor?.vendor_profile_photo)}
                  alt={vendor?.vendor_name || "Vendor"}
                  sx={{
                    width: isMobile ? "36px" : "50px",
                    height: isMobile ? "36px" : "50px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box sx={{ width: isMobile ? "36px" : "50px", height: isMobile ? "36px" : "50px", borderRadius: "50%", background: "#46979F", display: "grid", placeItems: "center" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "14px" : "20px", lineHeight: 1, color: "#FFFFFF", textTransform: "capitalize" }}>
                    {getInitials(vendor?.vendor_name)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "8px" : "10px" }}>
                <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "14px" : "18px", lineHeight: isMobile ? "11.195px" : 1, letterSpacing: isMobile ? "-0.28px" : "normal", color: "#1D1D1B", textTransform: "capitalize" }}>
                  {vendor?.vendor_name || ""}
                </Typography>
                <Box
                  onClick={() => {
                    const mapLink = sanitizeUrl(vendor?.location_link);
                    if (!mapLink) return;
                    window.open(mapLink, "_blank", "noopener,noreferrer");
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: vendor?.location_link ? "pointer" : "default",
                  }}
                >
                  <Box component="img" src={Images.gps} alt="location" sx={{ width: isMobile ? "16px" : "20px", height: isMobile ? "16px" : "20px" }} />
                  <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "16px", lineHeight: 1, color: "#686866", textTransform: "capitalize" }}>
                    {vendor?.vendor_location || ""}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ width: "fit-content", maxWidth: "100%", px: "14px", py: "8px", borderRadius: "36px", border: "0.267px solid #D1DFAD", background: "#F5F8ED", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              {verifiedVendor && (
                <>
                  <Box component="img" src={Images.greenBgTick} alt="verified" sx={{ width: "16px", height: "16px" }} />
                  <Typography sx={{ fontWeight: 600, fontSize: "12px", lineHeight: "18px", color: "#8EA946", textTransform: "capitalize" }}>
                    Verified Vendor
                  </Typography>
                </>
              )}
              {vendor?.vendor_experience ? (
                <>
                  {verifiedVendor && <Box sx={{ width: "1px", height: "18px", borderRadius: "50px", background: "#BDD188" }} />}
                  <Box component="img" src={Images.greenCalendar} alt="years" sx={{ width: "16px", height: "16px" }} />
                  <Typography sx={{ fontWeight: 600, fontSize: "12px", lineHeight: "18px", color: "#8EA946" }}>
                    {vendor.vendor_experience}
                  </Typography>
                </>
              ) : null}
            </Box>
          </Box>
          {isMiddleScreen && <Box sx={{ visibility: "hidden", borderRadius: "16px" }} />}
        </Box>
        {isMobile && <Box sx={{ width: "100%", px: "16px" }}>{descriptionAndStory}</Box>}
      </Box>
    </Box>
  );
};

export default VendorProductsSection;
